import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { selectAgent } from '../../store/actions/agentActions';
import { matchAgent } from '../../api/manifestApi';
import { AgentConfig } from '../../types/agent.types';
import { findAgentByNameOrId } from '../../utils/agentUtils';

interface MatchResult {
  agentName: string;
  reason: string;
  agent: AgentConfig | null;
}

interface AgentMatcher {
  /** Current user query text. */
  query: string;
  /** Update the query text. */
  setQuery: (value: string) => void;
  /** Whether a match request is in flight. */
  loading: boolean;
  /** The last successful match result, or null. */
  result: MatchResult | null;
  /** Error message from the last failed attempt, or null. */
  error: string | null;
  /** Submit the current query to the /match endpoint. */
  search: () => Promise<void>;
  /** Select the matched agent (setActiveAgent + switchToAgent). */
  selectAgent: () => void;
}

/**
 * Encapsulates the "find the right agent" flow:
 *  1. Call POST /match with a user query.
 *  2. Resolve the returned agent_name to an AgentConfig from the store.
 *  3. On demand, select that agent via Redux (no page reload).
 */
export const useAgentMatcher = (): AgentMatcher => {
  const dispatch = useAppDispatch();
  const agents = useAppSelector((state) => state.agent.agents);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolveAgent = useCallback(
    (name: string): AgentConfig | null => {
      return findAgentByNameOrId(agents, name) ?? null;
    },
    [agents],
  );

  const search = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const { agent_name, reason } = await matchAgent(trimmed);
      setResult({
        agentName: agent_name,
        reason,
        agent: resolveAgent(agent_name),
      });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query, loading, resolveAgent]);

  const doSelectAgent = useCallback(() => {
    if (!result?.agent) return;
    selectAgent(dispatch, result.agent);
  }, [result, dispatch]);

  return { query, setQuery, loading, result, error, search, selectAgent: doSelectAgent };
};
